import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { type ClipboardScorePenaltyMultipliers } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { formatDurationLabel } from "@src/appV2/redesign/utils/formatDurationLabel";
import { type Worker } from "@src/appV2/Worker/api/types";
import { differenceInMinutes, parseISO } from "date-fns";
import { capitalize, startCase } from "lodash";

import { type Consequence, type ConsequenceType } from "../components/Activity/ConsequencePill";
import { clipboardScoreActionLabelMap, ClipboardScoreActions } from "./constants";

export function isScoreHealthy(score: number): boolean {
  return score >= 100;
}

export function getActionLabel(log: ClipboardScoreLog): string {
  const { displayAction, action, shiftStart, clockInTime, leadTime } = log;

  const formatDurationOptions = { truncatePrecision: true };
  if (
    action === ClipboardScoreActions.START_SHIFT_LATE &&
    isDefined(shiftStart) &&
    isDefined(clockInTime)
  ) {
    const lateTimeInMinutes = differenceInMinutes(parseISO(clockInTime), parseISO(shiftStart), {
      roundingMethod: "floor",
    });
    return `Arrived ${formatDurationLabel(
      lateTimeInMinutes,
      "minute",
      formatDurationOptions
    )} late`;
  }

  if (action === ClipboardScoreActions.CANCEL_SHIFT && isDefined(leadTime)) {
    const roundedLeadTime = Math.ceil(leadTime);
    return `${formatDurationLabel(
      roundedLeadTime,
      "hour",
      formatDurationOptions
    )} notice cancellation`;
  }

  if (action === "CUSTOM_BONUS" && isDefined(displayAction) && displayAction.length > 0) {
    return displayAction;
  }

  const label = clipboardScoreActionLabelMap[action];

  if (isDefined(label)) {
    return label;
  }

  return startCase(capitalize(displayAction ?? action));
}

export function getConsequence(params: {
  action: ClipboardScoreActions;
  score: number;
  adjustment: number;
}): Consequence | undefined {
  const { action, score, adjustment } = params;

  if (action === ClipboardScoreActions.ACCOUNT_RE_ENROLL) {
    return undefined;
  }

  if (action === ClipboardScoreActions.NO_CALL_NO_SHOW) {
    return "Restriction";
  }

  if (adjustment === 0) {
    return adjustment;
  }

  if (score <= 0 && adjustment < 0 && score - adjustment > 0) {
    return adjustment;
  }

  if (score <= 0) {
    return "On Restriction";
  }

  return adjustment;
}

export function getConsequenceType(action: ClipboardScoreActions): ConsequenceType {
  return action === ClipboardScoreActions.WORK_SHIFT ||
    action === ClipboardScoreActions.CLOCKIN_TIMELINESS_BONUS ||
    action === ClipboardScoreActions.QUALITY_BONUS ||
    action === ClipboardScoreActions.CUSTOM_BONUS
    ? "bonus"
    : "penalty";
}

export function areMultipliersActive(multipliers?: ClipboardScorePenaltyMultipliers): boolean {
  return (
    isDefined(multipliers) && (multipliers.lateness.next > 1 || multipliers.cancellation.next > 1)
  );
}

export function isWorkerInviteRequired({ stage, substage }: Pick<Worker, "stage" | "substage">) {
  return stage === WorkerStage.RESTRICTED && substage?.name === "INVITE_REQUIRED";
}

export function isWorkerRestricted({ stage, substage }: Pick<Worker, "stage" | "substage">) {
  return stage === WorkerStage.RESTRICTED && substage?.name !== "INVITE_REQUIRED";
}

interface PolicyWithEmergencyCredit {
  points: number;
  pointsWithEmergencyCredit?: number;
}

export function calculateEmergencyCreditPointsSaved(
  clipboardScoreAdjustment: number | undefined | null,
  policy: PolicyWithEmergencyCredit[] | undefined
): number | undefined {
  if (!isDefined(clipboardScoreAdjustment) || !isDefined(policy) || policy.length === 0) {
    return undefined;
  }

  const absAdjustment = Math.abs(clipboardScoreAdjustment);

  // Find the matching policy entry where pointsWithEmergencyCredit matches the adjustment
  const matchingPolicy = policy.find((entry) => {
    if (!isDefined(entry.pointsWithEmergencyCredit)) {
      return false;
    }

    return Math.abs(entry.pointsWithEmergencyCredit) === absAdjustment;
  });

  if (!isDefined(matchingPolicy) || !isDefined(matchingPolicy.pointsWithEmergencyCredit)) {
    // No match found - policy may have changed
    return undefined;
  }

  // Calculate saved points: difference between regular penalty and emergency credit penalty
  const savedPoints =
    Math.abs(matchingPolicy.points) - Math.abs(matchingPolicy.pointsWithEmergencyCredit);

  return savedPoints > 0 ? savedPoints : undefined;
}
